// src/pages/Create.jsx
// CLAUDE.md "7. 영상 생성 파이프라인" — 7단계 순차 실행.

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  addDoc,
  collection,
  serverTimestamp,
} from 'firebase/firestore';

import { db } from '../firebase.js';
import { useAuth } from '../hooks/useAuth.js';
import { useCountry } from '../contexts/CountryContext.jsx';
import { usePlanGuard } from '../hooks/usePlanGuard.js';
import { api } from '../api/citizencast.js';
import { ANCHOR_VOICES, EMOTION_LABELS } from '../utils/anchorVoices.js';

import Stepper from '../components/Stepper.jsx';
import StanceCard from '../components/StanceCard.jsx';
import ProgressBar from '../components/ProgressBar.jsx';
import SEO from '../components/SEO.jsx';
import { ga } from '../utils/ga4.js';

const STEPPER_KEYS = ['step1Title', 'step2Title', 'step3Title', 'step4Title'];

const STAGES = [
  { percent: 5,  label: null },
  { percent: 15, label: null },
  { percent: 35, label: 'scriptWriting' },
  { percent: 55, label: 'mediaFetching' },
  { percent: 75, label: 'voiceSynth' },
  { percent: 90, label: 'rendering' },
  { percent: 100, label: 'done' },
];

export default function Create() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile, user } = useAuth();
  const { country, config } = useCountry();
  const plan = usePlanGuard();

  const isAdmin = (() => {
    const list = (import.meta.env.VITE_ADMIN_EMAILS || '')
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    return list.includes((profile?.email || '').toLowerCase());
  })();

  const [step, setStep] = useState(1);
  const [url, setUrl] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [article, setArticle] = useState(null);

  // 오늘의 뉴스 수집 (null=미수집, []=빈 결과, [...]=목록)
  const [news, setNews] = useState(null);
  const [loadingNews, setLoadingNews] = useState(false);

  const [stance, setStance] = useState(null);

  const [comment, setComment] = useState('');

  // 원클릭 어그로 훅 — LLM 이 추천하는 한줄평 후보 3개 (논쟁/촌철/감정)
  const [suggestions, setSuggestions] = useState([]);
  const [suggesting, setSuggesting] = useState(false);

  const [emotionTone, setEmotionTone] = useState('neutral');

  // Admin 전용: 상위 tier 이어도 워터마크 강제 표시 (홍보용)
  const [adminForceWatermark, setAdminForceWatermark] = useState(false);
  // Tier 3 전용: 해외 번역 송출 — 출력 언어/국가 선택 (null = 원본 country)
  const [targetLanguage, setTargetLanguage] = useState(null);

  const [busy, setBusy] = useState(false);
  const [progressPct, setProgressPct] = useState(0);
  const [progressLabel, setProgressLabel] = useState(null);

  const credits = profile?.credits ?? 0;

  // targetUrl 전달 시(뉴스 클릭) 그 링크로, 없으면 입력칸 url 로 분석.
  async function handleAnalyze(targetUrl) {
    const link = (typeof targetUrl === 'string' ? targetUrl : url).trim();
    if (!link) return;
    setUrl(link);
    setAnalyzing(true);
    setArticle(null);
    try {
      const res = await api.validateUrl(link);
      setArticle({
        title: res.title,
        content: res.content,
        publishedAt: res.publishedAt,
        imageUrls: res.imageUrls || [],
      });
      setStep(2);
    } catch (err) {
      toast.error(err.message || t('errors.notNews'));
    } finally {
      setAnalyzing(false);
    }
  }

  async function handleSuggestComments() {
    if (!article || !stance) {
      toast.error(t('create.suggestNeedStance'));
      return;
    }
    setSuggesting(true);
    try {
      const res = await api.suggestComments({
        newsTitle: article.title,
        newsContent: article.content,
        stance,
        country,
        emotionTone: plan.canSelectEmotionTone ? emotionTone : 'neutral',
        targetLanguage: plan.canCrossBorderTranslate ? targetLanguage : null,
      });
      setSuggestions(res.suggestions || []);
    } catch (err) {
      toast.error(err.message || t('create.suggestCommentsFailed'));
    } finally {
      setSuggesting(false);
    }
  }

  async function handleCollectNews() {
    setLoadingNews(true);
    try {
      const res = await api.newsFeed(country, { limit: 12 });
      setNews(res.news || []);
    } catch (err) {
      toast.error(err.message || t('create.newsFailed'));
    } finally {
      setLoadingNews(false);
    }
  }

  function applyProgress(idx) {
    const stage = STAGES[idx];
    setProgressPct(stage.percent);
    setProgressLabel(stage.label);
  }

  async function handleGenerate() {
    if (!article || !stance) return;
    if (!isAdmin && credits < 1) {
      toast.error(t('create.insufficientCredits'));
      return;
    }

    setBusy(true);
    applyProgress(0);

    let videoId = null;
    let creditDeducted = false;

    try {
      // 0) 한줄평 자동 정제 — 욕설/심한 표현만 순화, 원본 최대 보존 (사용자 동작 불필요)
      let finalComment = plan.canAddCustomComment ? comment.trim() : '';
      if (finalComment) {
        try {
          const refinedRes = await api.filterComment(finalComment, config.lang);
          if (refinedRes?.filtered) finalComment = refinedRes.filtered;
        } catch {
          // 정제 실패해도 원본으로 진행
        }
      }
      const effectiveTarget = plan.canCrossBorderTranslate ? targetLanguage : null;

      // 1) validate-url 은 이미 article 에 있으므로 스킵 (5% 표시만)
      applyProgress(0);

      // 2) filter-comment (이미 했으면 스킵)
      applyProgress(1);

      // 3) generate-script — addDoc 보다 먼저 호출. 이렇게 해야 script.scenes 를 처음부터 video doc 에
      // 담을 수 있다. firestore.rules 가 일반 사용자의 update 를 legalConsentAt/downloadedAt 으로
      // 제한해서 사후 script 패치가 불가능하기 때문.
      applyProgress(2);
      const scriptRes = await api.generateScript({
        newsTitle: article.title,
        newsContent: article.content,
        stance,
        country,
        userComment: finalComment || null,
        targetLanguage: effectiveTarget,
        plan: plan.plan,
        emotionTone: plan.canSelectEmotionTone ? emotionTone : 'neutral',
      });
      const script = scriptRes.script;

      // script 받은 후 video doc 생성 — addDoc 자체는 빠르고 (~100ms), 이후 흐름(media/voice/render)
      // 이 모두 videoId 를 필요로 하므로 여기서 만든다. scenes 포함 저장 → 사후 진단/재렌더 가능.
      const initial = {
        uid: user.uid,
        country,
        targetLanguage: effectiveTarget,
        newsUrl: url,
        newsTitle: article.title,
        stance,
        userComment: finalComment || null,
        emotionTone: plan.canSelectEmotionTone ? emotionTone : 'neutral',
        script,
        audioUrl: null,
        renderJobId: null,
        renderStatus: 'pending',
        videoUrl: null,
        creditUsed: 0,
        legalConsentAt: null,
        downloadedAt: null,
        createdAt: serverTimestamp(),
      };
      const ref = await addDoc(collection(db, 'videos'), initial);
      videoId = ref.id;

      // 4) 배경 결정 — 씬별 검색 우선. 기사 사진은 신뢰 불가 (광고/관련기사 썸네일 다수) → 사용 안함.
      applyProgress(3);

      // 기사 제목에서 핵심 주제 단어 추출 → 검색어 앞에 붙여 관련성 강화
      const articleSubject = (article?.title || '').split(/\s+/).slice(0, 4).join(' ');

      // 씬별 검색어 — 추상 씬도 articleSubject 로 폴백해서 검색 시도.
      // 검색이 비는 씬만 AI 폴백 (예전에는 추상 씬이라고 빈 문자열 보내서 SearchAPI 호출 자체를 안 했음).
      const queries = script.scenes.map((s) => {
        const q = (s.searchQuery || '').trim();
        return q || articleSubject || '';
      });
      const searchP = api
        .searchImages(queries, country, articleSubject)
        .catch((e) => {
          console.error('[bg] searchImages failed:', e?.message || e);
          return { images: [] };
        });

      // AI 와 Pexels 는 항상 병렬 호출 — 검색 빈 씬을 메우는 폴백
      const aiP = api.generateImage(script.scenes, country, []).catch(() => ({ images: [] }));
      const pexelsP = api.fetchMedia(script.scenes, country, effectiveTarget).catch(() => ({ media: [] }));

      const [searchRes, aiRes, pexelsRes] = await Promise.all([searchP, aiP, pexelsP]);
      const searchUrls = (searchRes.images || []).map((m) => m.url);
      // 진단 — 어디서 검색이 실패했는지 한눈에. (admin 에게만 토스트, 일반 사용자는 콘솔만)
      console.log('[bg] search diag:', searchRes.diag, 'urls:', searchUrls);
      if (isAdmin && searchRes.diag) {
        const d = searchRes.diag;
        toast(`검색: ${d.assignedScenes}/${d.totalScenes} 씬 매칭`, { duration: 4000 });
      }
      const aiBySc = Object.fromEntries((aiRes.images || []).map((m) => [m.sceneNumber, m]));
      const pexelsBySc = Object.fromEntries((pexelsRes.media || []).map((m) => [m.sceneNumber, m]));

      const media = script.scenes.map((s, i) => {
        // 1순위: 씬별 검색 (실제 뉴스 사진, 기사 주제 prepend 로 관련성 ↑)
        if (searchUrls[i]) return { sceneNumber: s.sceneNumber, type: 'photo', url: searchUrls[i] };
        // 2순위: AI 생성 (추상 씬 또는 검색이 비었을 때)
        const aiUrl = aiBySc[s.sceneNumber]?.url;
        if (aiUrl) return { sceneNumber: s.sceneNumber, type: 'photo', url: aiUrl };
        // 3순위: Pexels stock
        const pexels = pexelsBySc[s.sceneNumber];
        if (pexels?.url) return pexels;
        return { sceneNumber: s.sceneNumber, type: 'photo', url: null };
      });

      // 5) synthesize-voice
      applyProgress(4);
      // Cross-border 송출 시 target 언어의 voice 사용
      const voiceCountry = effectiveTarget || country;
      const voiceId = ANCHOR_VOICES[voiceCountry]?.[plan.canSelectEmotionTone ? emotionTone : 'neutral'];
      const voiceRes = await api.synthesizeVoice({
        scenes: script.scenes,
        voiceId,
        emotionTone: plan.canSelectEmotionTone ? emotionTone : 'neutral',
        videoId,
        uid: user.uid,
        language: voiceCountry, // TTS-SAFE 강제 변환 분기용
      });

      // 6) 크레딧 차감
      await api.deductCredit(user.uid, videoId);
      creditDeducted = true;

      // 7) render-video
      applyProgress(5);
      ga.videoStart(stance, country);
      await api.renderVideo({
        videoId,
        script,
        media,
        audioUrl: voiceRes.audioUrl,
        audioUrls: voiceRes.audioUrls,
        audioDurationSec: voiceRes.audioDurationSec,
        sceneDurations: voiceRes.sceneDurations,
        plan: plan.plan,
        country,
        logoUrl: profile?.channelLogoUrl || null,
        forceWatermark: isAdmin && adminForceWatermark,
      });

      applyProgress(6);

      // 결과 페이지로 이동 — onSnapshot 에서 webhook 결과 기다림
      navigate(`/video/${videoId}`);
    } catch (err) {
      console.error(err);
      toast.error(err.message || t('errors.renderFailed'));
      if (creditDeducted) {
        // 환불
        try {
          await api.deductCredit(user.uid, videoId).catch(() => {});
          await fetch('/api/deduct-credit', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uid: user.uid, videoId, refund: true }),
          });
        } catch {/* ignore */}
      }
      setBusy(false);
      setProgressPct(0);
    }
  }

  // ----- UI -----

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <SEO title={t('nav.create')} description={t('create.seoDesc')} path="/create" noIndex />
      <Stepper current={step} steps={STEPPER_KEYS} />

      {/* Step 1 — URL */}
      {step === 1 && (
        <section className="card space-y-3">
          <h2 className="text-xl font-bold">{t('create.step1Title')}</h2>

          {/* 오늘의 정치 뉴스 수집 — 본인 국가 트렌딩 뉴스에서 바로 고르기 */}
          <button
            className="btn-secondary w-full"
            onClick={handleCollectNews}
            disabled={loadingNews || analyzing}
          >
            {loadingNews
              ? t('create.collectingNews')
              : t('create.collectNews', { country: t(`countries.${country}`) })}
          </button>

          {news && (
            <div className="space-y-1 max-h-[360px] overflow-y-auto pr-1">
              {news.length === 0 ? (
                <p className="text-xs text-gray-400 py-3 text-center">{t('create.noNews')}</p>
              ) : (
                <>
                  <p className="text-xs text-gray-500">{t('create.newsHint')}</p>
                  {news.map((n) => (
                    <button
                      key={n.link}
                      onClick={() => handleAnalyze(n.link)}
                      disabled={analyzing}
                      className="w-full text-left rounded-lg border border-gray-200 bg-white p-2 text-xs hover:bg-gray-50 transition disabled:opacity-50"
                    >
                      <div className="font-bold line-clamp-2">{n.title}</div>
                      <div className="mt-1 text-gray-400">
                        {n.source}{n.date ? ` · ${n.date}` : ''}
                      </div>
                    </button>
                  ))}
                </>
              )}
            </div>
          )}

          {/* 또는 직접 링크 붙여넣기 */}
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <span className="flex-1 border-t" />
            <span>{t('create.orPasteUrl')}</span>
            <span className="flex-1 border-t" />
          </div>

          <input
            className="input"
            type="url"
            placeholder={t('create.urlPlaceholder')}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <button
            className="btn-primary w-full"
            onClick={() => handleAnalyze()}
            disabled={analyzing || !url.trim()}
          >
            {analyzing ? '…' : t('create.analyzeBtn')}
          </button>
          {article && (
            <div className="border rounded-xl p-3 bg-gray-50 text-sm">
              <div className="font-bold">{article.title}</div>
              {article.publishedAt && (
                <div className="text-xs text-gray-500">{article.publishedAt}</div>
              )}
              <p className="mt-2 text-gray-700 line-clamp-3">
                {article.content?.slice(0, 200)}…
              </p>
            </div>
          )}
        </section>
      )}

      {/* Step 2 — Stance */}
      {step === 2 && (
        <section className="card space-y-4">
          <h2 className="text-xl font-bold">{t('create.step2Title')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StanceCard
              stance="liberal"
              label={t('create.stanceLiberal')}
              selected={stance === 'liberal'}
              onSelect={setStance}
            />
            <StanceCard
              stance="conservative"
              label={t('create.stanceConservative')}
              selected={stance === 'conservative'}
              onSelect={setStance}
            />
            <StanceCard
              stance="factual"
              label={t('create.stanceFactual')}
              selected={stance === 'factual'}
              onSelect={setStance}
            />
          </div>
          <div className="flex justify-between">
            <button className="btn-secondary" onClick={() => setStep(1)}>←</button>
            <button
              className="btn-primary"
              onClick={() => setStep(3)}
              disabled={!stance}
            >
              →
            </button>
          </div>
        </section>
      )}

      {/* Step 3 — Comment + Emotion */}
      {step === 3 && (
        <section className="card space-y-4">
          <h2 className="text-xl font-bold">{t('create.step3Title')}</h2>

          {!plan.canAddCustomComment ? (
            <div className="border-2 border-dashed rounded-xl p-6 text-center">
              <div className="text-3xl mb-2">🔒</div>
              <p className="text-gray-700 mb-3">
                {t('create.commentLocked')}
              </p>
              <Link to="/pricing" className="btn-primary inline-block">
                {t('pricing.upgradeBtn')}
              </Link>
            </div>
          ) : (
            <>
              {/* 원클릭 어그로 훅 — 어르신 사용자도 타이핑 없이 카드 터치 한 번으로 한줄평 장착 */}
              <div className="space-y-2">
                <button
                  type="button"
                  className="btn-secondary w-full text-sm"
                  onClick={handleSuggestComments}
                  disabled={suggesting}
                >
                  {suggesting
                    ? t('create.suggestCommentsLoading')
                    : suggestions.length > 0
                      ? t('create.suggestCommentsRetry')
                      : t('create.suggestCommentsBtn')}
                </button>

                {suggestions.length > 0 && (
                  <>
                    <p className="text-xs text-gray-500">{t('create.suggestCommentsHint')}</p>
                    <div className="space-y-2">
                      {suggestions.map((s) => {
                        const labelKey = {
                          debate: 'suggestKindDebate',
                          sharp: 'suggestKindSharp',
                          emotional: 'suggestKindEmotional',
                        }[s.kind] || 'suggestKindSharp';
                        const selected = comment.trim() === s.text;
                        return (
                          <button
                            key={s.kind}
                            type="button"
                            onClick={() => setComment(s.text)}
                            className={`w-full text-left rounded-xl border p-3 transition ${
                              selected
                                ? 'border-transparent text-white'
                                : 'bg-white border-gray-200 hover:bg-gray-50'
                            }`}
                            style={selected ? { background: 'var(--color-primary)' } : {}}
                          >
                            <div className="text-xs font-bold opacity-80">{t(`create.${labelKey}`)}</div>
                            <div className="mt-1 text-base font-medium leading-snug">{s.text}</div>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              <textarea
                className="input min-h-[100px]"
                placeholder={t('create.commentPlaceholder')}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                maxLength={300}
              />
              <p className="text-xs text-gray-500">
                {t('create.autoFilterHint')}
              </p>

              {plan.canCrossBorderTranslate && (
                <div>
                  <label className="text-sm font-bold mb-1 block">{t('create.crossBorderLabel')}</label>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { val: null, label: t('create.crossBorderOriginal', { country }) },
                      { val: 'KR', label: '🇰🇷 KR' },
                      { val: 'US', label: '🇺🇸 US' },
                      { val: 'JP', label: '🇯🇵 JP' },
                    ].map((opt) => (
                      <button
                        key={String(opt.val)}
                        onClick={() => setTargetLanguage(opt.val)}
                        className={`px-2 py-2 rounded-xl text-sm border transition ${
                          targetLanguage === opt.val
                            ? 'text-white border-transparent'
                            : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                        style={
                          targetLanguage === opt.val
                            ? { background: 'var(--color-primary)' }
                            : {}
                        }
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{t('create.crossBorderHint')}</p>
                </div>
              )}

              <div>
                <label className="text-sm font-bold mb-1 block">
                  {t('create.emotionLabel', { tone: EMOTION_LABELS[emotionTone]?.[config.lang] || emotionTone })}
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {Object.keys(EMOTION_LABELS).map((tone) => (
                    <button
                      key={tone}
                      onClick={() => setEmotionTone(tone)}
                      className={`px-2 py-2 rounded-xl text-sm border transition ${
                        emotionTone === tone
                          ? 'text-white border-transparent'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                      style={
                        emotionTone === tone
                          ? { background: 'var(--color-primary)' }
                          : {}
                      }
                    >
                      {EMOTION_LABELS[tone]?.[config.lang] ?? tone}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="flex justify-between">
            <button className="btn-secondary" onClick={() => setStep(2)}>←</button>
            <button className="btn-primary" onClick={() => setStep(4)}>→</button>
          </div>
        </section>
      )}

      {/* Step 4 — Generate */}
      {step === 4 && (
        <section className="card space-y-4">
          <h2 className="text-xl font-bold">{t('create.step4Title')}</h2>

          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">
              {t('create.creditsLeft', { count: credits })}
            </span>
            <Link
              to="/pricing"
              className="text-xs underline"
              style={{ color: 'var(--color-primary)' }}
            >
              {t('nav.pricing')} →
            </Link>
          </div>

          <div className="rounded-xl p-3 bg-gray-50 text-sm space-y-1">
            <div><strong>📰</strong> {article?.title}</div>
            <div><strong>🎭</strong> {t(`create.stance${stance?.charAt(0).toUpperCase()}${stance?.slice(1)}`)}</div>
            {plan.canAddCustomComment && comment && (
              <div><strong>💬</strong> {comment}</div>
            )}
          </div>

          {isAdmin && (
            <label className="flex items-center gap-2 text-xs text-gray-600 border border-dashed rounded-xl px-3 py-2">
              <input
                type="checkbox"
                checked={adminForceWatermark}
                onChange={(e) => setAdminForceWatermark(e.target.checked)}
              />
              <span>🛠 admin: 워터마크 강제 표시 (홍보용 — tier2/tier3 에서도 워터마크 박힘)</span>
            </label>
          )}

          {busy ? (
            <ProgressBar percent={progressPct} labelKey={progressLabel} />
          ) : (
            <button
              className="btn-primary w-full text-lg"
              onClick={handleGenerate}
              disabled={busy || !article || !stance || (credits < 1 && !isAdmin)}
            >
              🎬 {t('create.generateBtn')}
            </button>
          )}

          <div className="flex justify-between">
            <button
              className="btn-secondary"
              onClick={() => setStep(plan.canAddCustomComment ? 3 : 2)}
              disabled={busy}
            >
              ←
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
